import { Info, MessageSquare, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type ProductionDeclarationMaterialCommodityOriginCardProps = {
  actionDisabled?: boolean;
  actionLabel?: string;
  children?: ReactNode;
  description: string;
  onActionClick?: () => void;
  onCommentClick?: () => void;
  tooltipAriaLabel?: string;
  tooltipContent?: ReactNode;
  title: string;
};

export function ProductionDeclarationMaterialCommodityOriginCard({
  actionDisabled,
  actionLabel,
  description,
  children,
  onActionClick,
  onCommentClick,
  tooltipAriaLabel,
  tooltipContent,
  title,
}: ProductionDeclarationMaterialCommodityOriginCardProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  return (
    <div className='rounded-[1.4rem] border border-declaration-border bg-white px-7 py-8 shadow-[0_10px_24px_rgba(17,24,39,0.06)] sm:px-8'>
      <div className='flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between'>
        <div className='space-y-6'>
          <div className='flex items-center gap-2.5'>
            <h4 className='font-semibold text-[1.2rem] text-declaration-foreground leading-none tracking-tight'>
              {title}
            </h4>
            {tooltipContent ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label={tooltipAriaLabel ?? title}
                    className='rounded-sm text-declaration-muted-foreground transition-colors hover:text-declaration-foreground-muted'
                    type='button'
                  >
                    <Info aria-hidden className='size-5' />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  align='start'
                  className='w-[min(44rem,calc(100vw-2rem))] space-y-3 rounded-xl border border-declaration-border bg-white p-4 text-left text-declaration-foreground text-sm leading-6 shadow-[0_10px_24px_rgba(17,24,39,0.08)] dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'
                  collisionPadding={24}
                  showArrow={false}
                  side='bottom'
                  sideOffset={12}
                >
                  {tooltipContent}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Info
                aria-hidden
                className='size-5 text-declaration-muted-foreground'
              />
            )}
          </div>
          <p className='max-w-[38rem] text-base text-declaration-muted-foreground leading-7'>
            {description}
          </p>
        </div>

        <div className='flex flex-wrap gap-2'>
          {onCommentClick ? (
            <Button onClick={onCommentClick} type='button' variant='outline'>
              <MessageSquare className='size-5' strokeWidth={2.2} />
              {t('materialCommodityOrigins.comments.add')}
            </Button>
          ) : null}
          {actionLabel ? (
            <Button
              className='h-11 rounded-lg border-declaration-border bg-white px-5 font-medium text-declaration-foreground shadow-none hover:bg-declaration-hover dark:border-declaration-border dark:bg-white dark:text-declaration-foreground dark:hover:bg-declaration-hover'
              disabled={actionDisabled ?? !onActionClick}
              onClick={onActionClick}
              type='button'
              variant='outline'
            >
              <Plus className='size-5' strokeWidth={2.2} />
              {actionLabel}
            </Button>
          ) : null}
        </div>
      </div>

      {children ? <div className='mt-8'>{children}</div> : null}
    </div>
  );
}
